'use client';
import * as React from 'react';
import { useTranslations } from 'next-intl';
import type { BoxT } from '@mentimeter/ragnar-ui/box';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Button } from '@mentimeter/ragnar-ui/button';
import {
  ModalBody,
  ModalContainer,
  ModalRoot,
  ModalTrigger,
} from '@mentimeter/ragnar-ui/modal';
import type { VariantT } from '@mentimeter/ragnar-ui/button';
import type { EnterpriseFormPlacementT } from 'src/types';
import { H2, P } from '@mentimeter/content-ui/Typography';
import SalesForceForm from './SalesForceForm';

interface ContactUsModalT {
  onClick?: () => void;
  text?: string | undefined;
  variant?: VariantT;
  size?: 'large' | 'default' | 'compact';
}

/* Use this if a modal is wanted to open the form*/
export const ContactUs = ({
  onClick,
  text,
  variant = 'tertiary',
  size = 'large',
}: ContactUsModalT) => {
  const t = useTranslations('first_impression.legacy.contact_us');
  const [show, setShow] = React.useState(false);

  return (
    <ModalRoot open={show} onOpenChange={setShow}>
      <ModalTrigger>
        <Button
          variant={variant}
          size={size}
          onClick={() => {
            onClick?.();
            setShow(true);
          }}
        >
          {text ?? t('contact_us')}
        </Button>
      </ModalTrigger>
      <ModalContainer mr="space0" maxWidth={[374, 469]}>
        <ModalBody>
          <ContactUsForm p="space4" placement="cta" />
        </ModalBody>
      </ModalContainer>
    </ModalRoot>
  );
};

interface ContactUsFormProps extends BoxT {
  placement?: EnterpriseFormPlacementT;
}

/* Use this if only the form is wanted */
export const ContactUsForm = ({
  placement = 'hero',
  ...rest
}: ContactUsFormProps) => {
  const t = useTranslations('first_impression.legacy.contact_us');
  const [hasSentForm, setHasSentForm] = React.useState(false);

  return (
    <Box borderRadius={1} overflow="hidden" {...rest}>
      <Box maxWidth="400px" alignItems="center">
        {hasSentForm ? (
          <>
            <H2>{t('thank_you')}</H2>
            <P textAlign="center" mb="space0">
              {t('will_reach_out')}
            </P>
          </>
        ) : (
          <SalesForceForm
            onHasSentForm={setHasSentForm}
            placement={placement}
          />
        )}
      </Box>
    </Box>
  );
};
